<script lang="ts" setup>
import { computed, defineAsyncComponent, ref, watch } from 'vue'
import { useRoute, useRouter } from 'vue-router/composables'
import type { HomePageConfig } from '../configs/types'
import { xiaoyaoConfig } from '../configs/common'
import ModuleSlideBook from './modules/ModuleSlideBook.flipnovel.vue'
import ModuleGrid2x4 from './modules/ModuleGrid2x4.flipnovel.vue'
import ModuleFeatured1Plus4 from './modules/ModuleFeatured1Plus4.flipnovel.vue'
import ModuleGrid1x4 from './modules/ModuleGrid1x4.flipnovel.vue'
import ModuleFeed from './modules/ModuleFeed.flipnovel.vue'
import ModuleTags2x3 from './modules/ModuleTags2x3.flipnovel.vue'
import ModuleRanking from './modules/ModuleRanking.flipnovel.vue'
import ModuleSwiper from './modules/ModuleSwiper.flipnovel.vue'
import ModuleBanner from './modules/ModuleBanner.flipnovel.vue'
import { memberConfig as flipnovelMemberConfig } from '@/pages/member/configs/flipnovel'
import { memberConfig as moonpageMemberConfig } from '@/pages/member/configs/moonpage'
import { memberConfig as noveluneMemberConfig } from '@/pages/member/configs/novelune'
import { bannerShow } from '@/composables/useBannerShow'
import FlipnovelCenteredLoading from '@/components/FlipnovelCenteredLoading.vue'
import { COLUMN_TYPE } from '@/constants'
import { brand, texts as brandTexts, theme as brandTheme } from '@/config/brands'
import xiaoyaoIcon from '@/assets/xiaoyao.png'

const props = defineProps<{
  config: HomePageConfig
  setInfiniteRef: (el: HTMLElement | null) => void
  setInputRef: (el: any) => void
  input: string
  inputFocused: boolean
  pageList: any[]
  pageInfo: any
  skeleton: boolean
  modules: any[]
  currentTagId: string
  feeds: any[]
  feedShowName: string
  feedId: string
  feedsColumnType: string | number
  feedFetching: boolean
  feedEnding: boolean
  switchNavTrack: (item: any) => void
  onSearch: () => void
  gotoCollection: (id: string) => void
  gotoTags: (id: string) => void
  gotoBook: (id: string, firstChapterId?: string) => void
  getCurrentTagResources: (col: any) => any[]
  getCurrentShowTags: (col: any) => any[]
  handleTagClick: (tagId: string) => void
}>()

const emit = defineEmits<{
  (event: 'update:input', value: string): void
}>()

const drawerOpen = ref(false)
const route = useRoute()
const router = useRouter()
const MemberDrawerPage = defineAsyncComponent(() => import('@/pages/member/components/MemberPage.flipnovel.vue'))
const memberDrawerConfig = brand === 'moonpage'
  ? moonpageMemberConfig
  : brand === 'novelune'
    ? noveluneMemberConfig
    : flipnovelMemberConfig

// 路由变化时自动关闭抽屉
watch(() => route.path, () => {
  drawerOpen.value = false
})

function onSearchIconClick() {
  router.push({
    name: 'search',
    query: {
      st: String(Date.now()),
      autofocus: '1',
    },
  })
}

// 定义样式变量供 CSS v-bind 使用
const homeBgUrl = computed(() => `url(${props.config.theme.homeBg})`)
const tabActiveTextColor = computed(() => props.config.theme.tabActiveColor || 'var(--un-preset-theme-colors-n-text-1)')
const tabUnderlineColor = computed(() => props.config.theme.primaryColor || 'var(--un-preset-theme-colors-n-primary)')
const tabActiveTextGradient = computed(() => props.config.theme.primaryGradient || 'var(--un-preset-theme-colors-n-primary-gradient)')
const stickyHeaderBg = computed(() => props.config.theme.stickyHeaderBg || 'transparent')
const tabBorderColor = computed(() => props.config.theme.tabBorderColor || 'rgba(255, 255, 255, 0.05)')
const tabBackground = computed(() => props.config.theme.tabBackground || 'rgba(255, 255, 255, 0.015)')
const tabTextColor = computed(() => props.config.theme.tabTextColor || 'rgba(248, 247, 245, 0.68)')
const tabActiveBorderColor = computed(() => props.config.theme.tabActiveBorderColor || 'rgba(255, 138, 76, 0.42)')
const tabActiveBackground = computed(() => props.config.theme.tabActiveBackground || 'rgba(255, 138, 76, 0.12)')
const headerIconColor = computed(() => props.config.theme.headerIconColor || 'rgba(248, 247, 245, 0.95)')
// Keep the first module below the fixed header at every rem scale. The header
// is made up of the 46px top row, the 30px tab row, and two raw 12px tab
// paddings; the module itself has a 12px top margin.
const homeContentPaddingTop = computed(() => 'calc(1.2266667rem + 0.8rem + 24px - 0.32rem)')
const homeHeaderTitle = computed(() => brandTexts.appName || brandTexts.clientGroupName || 'Novel')
const homeHeaderBrandImage = computed(() => `/images/${brand}/title.png`)
const headerRowShadow = computed(() => 'none')
const tabRowPaddingY = computed(() => '12px')

// 判断是否显示逍遥联动图标
const showXiaoyaoIcon = computed(() => {
  const currentHost = window.location.host
  return xiaoyaoConfig.allowedDomains.includes(currentHost)
})

// 处理输入更新
function handleInput(event: Event) {
  emit('update:input', (event.target as HTMLInputElement).value)
}

function openDrawer() {
  drawerOpen.value = true
}

function closeDrawer() {
  drawerOpen.value = false
}

// 模块卡片背景色（从品牌配置读取，确保纯色）
const moduleCardBg = computed(() => brandTheme.bgCard || '#1D232D')

// 顶部 Banner: 仅当 modules[0].columnType === BANNER 时生效
const bannerModule = computed(() => {
  const first = props.modules[0]
  return first?.columnType === COLUMN_TYPE.BANNER ? first : null
})
const restModules = computed(() => bannerModule.value ? props.modules.slice(1) : props.modules)

// Flipnovel 专属模块卡片样式（深色大圆角卡片）
const moduleCardClass = 'px-3 py-4 rounded-2xl overflow-hidden rr-home-card-bg'
const moduleHeaderClass = 'mb-2px flex items-baseline justify-between'
</script>

<template>
  <div :ref="setInfiniteRef" class="h-100dvh overflow-y-scroll">
    <!-- Fixed Header -->
    <div
      v-show="!inputFocused"
      class="fixed top-0 left-0 right-0 z-20 bg-n-bg-global rr-home-header"
      style="max-width: 750px; margin: 0 auto;"
    >
      <!-- Top row: Search | Brand | Gift + Menu -->
      <div class="relative flex h-46px items-center justify-between px-15px rr-header-main-row">
        <div class="flex min-w-0 items-center justify-start gap-10px">
          <div class="w-8 h-8 flex items-center justify-center" @click="onSearchIconClick">
            <i-assets-icon-search class="w-5 h-5 rr-header-icon" />
          </div>
          <img
            :src="homeHeaderBrandImage"
            :alt="homeHeaderTitle"
            class="h-24px max-w-160px w-auto object-contain"
          >
        </div>
        <div class="flex items-center justify-end gap-10px -mr-1">
          <div class="w-8 h-8 flex items-center justify-center" @click="router.push({ name: 'welfare' })">
            <svg class="w-5 h-5 rr-header-icon" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round">
              <rect x="3" y="7" width="18" height="14" rx="1.5" />
              <path d="M3 11h18" />
              <path d="M12 7v14" />
              <path d="M12 7c-1-3-3.5-4.5-5.5-3-2 1.5-1 4.5 5.5 3" />
              <path d="M12 7c1-3 3.5-4.5 5.5-3 2 1.5 1 4.5-5.5 3" />
            </svg>
          </div>
          <div class="w-8 h-8 flex items-center justify-center" @click="openDrawer">
            <svg class="rr-menu-icon w-5 h-5 block" viewBox="0 0 20 20" fill="none" aria-hidden="true">
              <path d="M2.5 4.5H17.5" stroke="currentColor" stroke-width="2" stroke-linecap="round" />
              <path d="M2.5 10H17.5" stroke="currentColor" stroke-width="2" stroke-linecap="round" />
              <path d="M2.5 15.5H17.5" stroke="currentColor" stroke-width="2" stroke-linecap="round" />
            </svg>
          </div>
        </div>
      </div>

      <!-- Tab row -->
      <div class="overflow-x-auto px-15px rr-tab-row">
        <div class="flex items-center gap-8px">
          <!-- 逍遥联动图标 -->
          <img v-if="showXiaoyaoIcon" :src="xiaoyaoIcon" alt="xiaoyao" class="h-22px flex-shrink-0">
          <div v-for="item in pageList" :key="item.id">
            <div class="home-tab relative" :class="{ active: item.id === pageInfo.id }" @click="switchNavTrack(item)">
              <span class="text-14px whitespace-nowrap text-n-text-2">{{ item.showName }}</span>
            </div>
          </div>
        </div>
      </div>
    </div>

    <!-- Right Drawer -->
    <transition name="drawer-fade">
      <div v-if="drawerOpen" class="fixed inset-0 z-50" @click="closeDrawer">
        <div class="absolute inset-0 bg-black opacity-50" />
      </div>
    </transition>
    <transition name="drawer-slide">
      <div
        v-if="drawerOpen"
        class="fixed top-0 right-0 bottom-0 z-50 w-80vw max-w-320px bg-n-bg-global overflow-y-scroll"
      >
        <MemberDrawerPage :config="memberDrawerConfig" in-drawer @close="closeDrawer" />
      </div>
    </transition>

    <div class="home-bg px-15px pb-4" :style="{ backgroundImage: `url(${config.theme.homeBg})`, paddingTop: homeContentPaddingTop }">
      <!-- Search -->
      <div
        class="fixed top-0 left-0 right-0 z-20 px-15px pt-15px bg-n-bg-global transition-transform duration-300"
        :class="inputFocused ? 'translate-y-0' : '-translate-y-full'"
      >
        <div class="flex items-center space-x-5 relative z-10">
          <i-assets-icon-search class="w-4 h-4 absolute left-3 top-2.5 text-n-text-2" />
          <input
            :ref="el => setInputRef(el)"
            :value="input" enterkeyhint="search"
            class="!ml0 flex-1 h-9 w-full pl-9 pr-4 rounded-full border-0 !outline-0 truncate caret-n-primary placeholder:text-14px bg-n-bg-card"
            :placeholder="config.texts.searchPlaceholder" @input="handleInput" @keyup.enter="onSearch"
          >
        </div>
        <div v-if="inputFocused" class="fixed inset-0 w-full h-100vh z-9 bg-n-bg-card" />
      </div>

      <!-- 顶部 Banner -->
      <div v-if="bannerModule" class="rr-module-block">
        <ModuleBanner
          :module="bannerModule"
          :config="config"
          position="top"
          :goto-book="gotoBook"
        />
        <BizDownloadApp v-if="bannerShow" position="block" from="home" />
      </div>

      <!-- Modules -->
      <FlipnovelCenteredLoading v-if="skeleton" />
      <div v-else v-show="!inputFocused">
        <div v-for="col in restModules" :key="col.id" class="rr-module-block">
          <!-- 非顶部 Banner -->
          <template v-if="col.columnType === COLUMN_TYPE.BANNER">
            <ModuleBanner
              :module="col"
              :config="config"
              position="inline"
              :goto-book="gotoBook"
            />
            <BizDownloadApp v-if="bannerShow" position="block" from="home" />
          </template>
          <template v-else-if="col.columnType === COLUMN_TYPE.SLIDE_BOOK">
            <ModuleSlideBook
              :module="col"
              :config="config"
              :goto-book="gotoBook"
              :goto-collection="gotoCollection"
              :card-class="moduleCardClass"
              :header-class="moduleHeaderClass"
            />
            <BizDownloadApp v-if="bannerShow" position="block" from="home" />
          </template>
          <template v-else-if="col.columnType === COLUMN_TYPE.ROW_2_MUL_COL_3">
            <ModuleTags2x3
              :module="col"
              :config="config"
              :current-tag-id="currentTagId"
              :goto-book="gotoBook"
              :goto-tags="gotoTags"
              :get-current-tag-resources="getCurrentTagResources"
              :get-current-show-tags="getCurrentShowTags"
              :handle-tag-click="handleTagClick"
              :card-class="moduleCardClass"
              :header-class="moduleHeaderClass"
            />
          </template>
          <template v-else-if="col.columnType === COLUMN_TYPE.ROW_1_PLUS_COL_4">
            <ModuleFeatured1Plus4
              :module="col"
              :config="config"
              :goto-book="gotoBook"
              :goto-collection="gotoCollection"
              :card-class="moduleCardClass"
              :header-class="moduleHeaderClass"
            />
            <BizDownloadApp v-if="bannerShow" position="block" from="home" />
          </template>
          <template v-else-if="col.columnType === COLUMN_TYPE.ROW_2_MUL_COL_4">
            <ModuleGrid2x4
              :module="col"
              :config="config"
              :goto-book="gotoBook"
              :goto-collection="gotoCollection"
              :card-class="moduleCardClass"
              :header-class="moduleHeaderClass"
            />
            <BizDownloadApp v-if="bannerShow" position="block" from="home" />
          </template>
          <template v-else-if="col.columnType === COLUMN_TYPE.ROW_1_MUL_COL_4">
            <ModuleGrid1x4
              :module="col"
              :config="config"
              :goto-book="gotoBook"
              :goto-collection="gotoCollection"
              :card-class="moduleCardClass"
              :header-class="moduleHeaderClass"
            />
            <BizDownloadApp v-if="bannerShow" position="block" from="home" />
          </template>
          <template v-else-if="col.columnType === COLUMN_TYPE.ROW_3_MUL_COL_4">
            <ModuleRanking
              :module="col"
              :config="config"
              :card-class="moduleCardClass"
              :header-class="moduleHeaderClass"
            />
            <BizDownloadApp v-if="bannerShow" position="block" from="home" />
          </template>
          <template v-else-if="col.columnType === COLUMN_TYPE.SWIPER">
            <ModuleSwiper
              :module="col"
              :config="config"
              :card-class="moduleCardClass"
              :header-class="moduleHeaderClass"
            />
            <BizDownloadApp v-if="bannerShow" position="block" from="home" />
          </template>
        </div>
      </div>

      <!-- Feed -->
      <ModuleFeed
        v-if="feeds.length"
        :key="pageInfo.id"
        class="mt-12px"
        :feeds="feeds"
        :feed-show-name="feedShowName"
        :feed-id="feedId"
        :feeds-column-type="feedsColumnType"
        :feed-fetching="feedFetching"
        :feed-ending="feedEnding"
        :config="config"
        :goto-book="gotoBook"
        :card-class="moduleCardClass"
        :header-class="moduleHeaderClass"
      />
    </div>
  </div>
</template>

<style lang="scss" scoped>
.home-bg {
  background-image: v-bind(homeBgUrl);
  background-repeat: no-repeat;
  background-position: top left;
  background-size: 100% auto;
}

.home-tab {
  cursor: pointer;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  min-width: 74px;
  height: 30px;
  padding: 0 12px;
  border-radius: 999px;
  border: 1px solid v-bind(tabBorderColor);
  background: v-bind(tabBackground);
  transition: background-color 0.2s ease, border-color 0.2s ease, color 0.2s ease;
}

.home-tab span {
  color: v-bind(tabTextColor);
  font-size: 13px;
  line-height: 18px;
  font-weight: 500;
}

.home-tab.active {
  border-color: v-bind(tabActiveBorderColor);
  background: v-bind(tabActiveBackground);
}

.home-tab.active span {
  color: transparent;
  background-image: v-bind(tabActiveTextGradient);
  -webkit-background-clip: text;
  background-clip: text;
  font-weight: 700;
}

.fixed.top-0.left-0.right-0.z-20.bg-n-bg-global.rr-home-header {
  background: v-bind(stickyHeaderBg) !important;
  backdrop-filter: none !important;
  -webkit-backdrop-filter: none !important;
  border-bottom: 0 !important;
  box-shadow: none !important;
}

.rr-header-main-row {
  box-shadow: v-bind(headerRowShadow);
}

.rr-tab-row {
  background: transparent;
  padding-top: v-bind(tabRowPaddingY);
  padding-bottom: v-bind(tabRowPaddingY);
}

.rr-module-block {
  margin-top: 12px;
}

.rr-menu-icon {
  color: v-bind(headerIconColor);
}

.rr-header-icon {
  color: v-bind(headerIconColor);
}

:deep(.rr-header-icon path) {
  fill: currentColor !important;
}

:deep(.rr-home-card-bg) {
  background-color: v-bind(moduleCardBg);
}

/* Drawer animations */
.drawer-fade-enter-active,
.drawer-fade-leave-active {
  transition: opacity 0.3s ease;
}

.drawer-fade-enter,
.drawer-fade-leave-to {
  opacity: 0;
}

.drawer-slide-enter-active,
.drawer-slide-leave-active {
  transition: transform 0.3s ease;
}

.drawer-slide-enter,
.drawer-slide-leave-to {
  transform: translateX(100%);
}
</style>
